import { publish } from 'pub-sub';
import { logTimesync } from 'logger';
import delay from 'lodash-es/delay';
import svc from './svc';

export interface TimeSyncOpts {
	requiredResponses?: number;
	resyncTime?: number;
}

export class TimeSync {
	latency: number = 0;
	offset: number = 0;
	requiredResponses: number;
	resyncTime: number;
	private handle: any = null;
	private serverTimes: number[] = [];
	private workLatency: number[] = [];

	constructor(opts: TimeSyncOpts = {}) {
		this.requiredResponses = opts.requiredResponses || 0;
		this.resyncTime = opts.resyncTime || 10;
	}

	sync(): void {
		// If the time was set within the last x minutes,
		// ignore ntp set request. Time was sync'd recently enough.
		try {
			const t: number = this.offset;
			const d: number = this.fixTime() - t;
			if (d < 1000 * 60 * this.resyncTime) {
				return;
			} // x minutes; return==skip
		} catch (e) {
			// ignore any math exceptions
		}

		this.serverTimes = [];
		this.workLatency = [];
		this.getServerTime();
	}

	cancel(): void {
		if (this.handle) {
			clearInterval(this.handle);
			this.handle = null;
		}
	}

	getNow(): number {
		return new Date().getTime();
	}

	parseServerResponse(data: { offset: number; originaltime: number }): void {
		const origtime: number = data.originaltime;
		const delayamt: number = (this.getNow() - origtime) / 2;
		const offset: number = data.offset - delayamt;
		this.serverTimes.push(offset);
		this.workLatency.push(delayamt);

		// if we have enough responses set the data
		if (this.serverTimes.length >= this.requiredResponses) {
			// build average
			let average: number = this.serverTimes.reduce((prev, curr): number => prev + curr, 0);
			this.offset = Math.round(average / this.serverTimes.length);

			average = this.workLatency.reduce((prev, curr): number => prev + curr, 0);
			this.latency = Math.round(average / this.workLatency.length);

			logTimesync.info(() => `latency: ${this.latency}ms; drift: ${this.offset}ms`);

			publish('timesync:updated', {
				latency: this.latency,
				drift: this.offset
			});
		} else {
			delay(this.getServerTime.bind(this), 100);
		}
	}

	getServerTime(): void {
		svc.server_time({
			clienttime: this.getNow()
		}).then(this.parseServerResponse.bind(this));
	}

	fixTime(timeStamp?: number): number {
		const stamp: number = timeStamp || this.getNow();
		return stamp + this.offset;
	}

	now(): Date {
		return new Date(this.getNow() + this.offset);
	}

	startup(): void {
		const waitfor: number = 1000 * 60 * this.resyncTime;
		this.sync();
		this.handle = setInterval(this.sync.bind(this), waitfor);
	}
}

// Since timesync is intended to be a singleton, the instantiated object
// is normally the only thing of interest.
export const timesync: TimeSync = new TimeSync();
export default timesync;
